import { ExpandableSection, Flex, smBumps, Typo } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import { getAdditionalProfiles, InsuredProfile, Quote } from '../../../domain'
import { useExpandableSectionConfig } from '../../../hooks/adjustments/expandableSection'
import { toAdditionalProfileProposalData } from '../../../hooks/adjustments/proposal/additionalProfile/data'
import {
  tDefaults,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useAdjustments } from '../../../state'
import { AdditionalProfileManagementConfig } from '../../../state/configuration'
import { FormFormatter, isAnsweredInput } from '../../formBuilder'

const AdditionalProfileSummary: FunctionComponent<{
  summary: AdditionalProfileManagementConfig
  profile: InsuredProfile
  tKey: TKeyBuilder
}> = ({ summary, profile, tKey }) => {
  const { t } = useWriskTranslation()

  const { getAdjustment } = useAdjustments()

  const toData = toAdditionalProfileProposalData(profile)

  const [header, inputs] = useExpandableSectionConfig({
    sectionId: summary.id,
    form: summary.form,
    tKey,
    toData,
  })

  const expandedComponents = inputs
    .filter((input) => {
      const adjustment = getAdjustment(input.name)
      return isAnsweredInput(toData(adjustment))
    })
    .map((input) => {
      const adjustment = getAdjustment(input.name)

      return (
        <Flex
          key={input.name}
          px={smBumps}
          py={2}
          flexDirection='column'
          alignItems='flex-start'
        >
          <Typo typoSize='xs' color='bodySecondary' mb={1}>
            {t(tDefaults('product.headers', input.name))}
          </Typo>
          <Typo as='pre'>
            <FormFormatter input={input} value={toData(adjustment)} t={t} />
          </Typo>
        </Flex>
      )
    })

  return (
    <ExpandableSection reversed header={<Typo fontWeight='bold'>{header}</Typo>}>
      {expandedComponents}
    </ExpandableSection>
  )
}

export const AdditionalProfilesSummary: FunctionComponent<{
  config: AdditionalProfileManagementConfig
  quote: Quote
  tKey: TKeyBuilder
}> = ({ config, quote, tKey }) => {
  const additionalProfiles = getAdditionalProfiles(quote)

  const sections = additionalProfiles.map((it) => (
    <AdditionalProfileSummary
      key={it.profileCode}
      summary={config}
      profile={it}
      tKey={tKey}
    />
  ))

  return <>{sections}</>
}
